/**
 * Tema senkronizasyonu — karanlık/açık geçişlerde ikon renklerini otomatik günceller.
 *
 * Uygulamada tema, <html>/<body> üzerindeki `.light` sınıfıyla (veya sınıf yoksa
 * sistem tercihiyle) belirlenir. Bu düzenek çözümlenen temayı
 * `<html data-theme="dark|light">` olarak yazar; index.css içindeki
 * --picker-icon-filter / --search-clear-filter gibi tokenlar buna bağlı olduğu
 * için native takvim/saat/arama ikonları geçişte anında yeniden renklenir.
 *
 * Ayrıca her değişimde `app-theme:changed` CustomEvent'i yayınlanır; JS tarafında
 * ikon rengine bağlı bileşenler bunu dinleyebilir.
 */

export type ResolvedTheme = "dark" | "light";

const EVENT = "app-theme:changed";

export function resolveTheme(): ResolvedTheme {
  if (typeof document === "undefined") return "dark";
  const root = document.documentElement;
  if (root.classList.contains("light") || document.body?.classList.contains("light")) {
    return "light";
  }
  if (root.classList.contains("dark") || document.body?.classList.contains("dark")) {
    return "dark";
  }
  // Sınıf yoksa sistem tercihine düş
  if (typeof window !== "undefined" && window.matchMedia) {
    return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
  }
  return "dark";
}

export function applyResolvedTheme(): ResolvedTheme {
  const theme = resolveTheme();
  if (typeof document === "undefined") return theme;
  const root = document.documentElement;
  if (root.getAttribute("data-theme") !== theme) {
    root.setAttribute("data-theme", theme);
    root.style.colorScheme = theme;
    window.dispatchEvent(new CustomEvent<{ theme: ResolvedTheme }>(EVENT, { detail: { theme } }));
  }
  return theme;
}

/** Uygulama açılışında bir kez çağrılır. Temizleyici döndürür. */
export function initThemeIconSync(): () => void {
  if (typeof document === "undefined") return () => {};

  applyResolvedTheme();

  // 1) .light / .dark sınıf değişimlerini izle
  const observer = new MutationObserver(() => applyResolvedTheme());
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
  if (document.body) {
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });
  }

  // 2) Sistem teması değişimini izle (sınıf yoksa geçerli)
  const mql = window.matchMedia?.("(prefers-color-scheme: light)");
  const onMedia = () => applyResolvedTheme();
  mql?.addEventListener?.("change", onMedia);

  // 3) Sekme yeniden görünür olduğunda doğrula
  const onVisible = () => {
    if (document.visibilityState === "visible") applyResolvedTheme();
  };
  document.addEventListener("visibilitychange", onVisible);

  return () => {
    observer.disconnect();
    mql?.removeEventListener?.("change", onMedia);
    document.removeEventListener("visibilitychange", onVisible);
  };
}

/** İkon rengine bağlı bileşenler için abonelik yardımcısı. */
export function onThemeChange(handler: (theme: ResolvedTheme) => void): () => void {
  const listener = (e: Event) => handler((e as CustomEvent<{ theme: ResolvedTheme }>).detail.theme);
  window.addEventListener(EVENT, listener);
  return () => window.removeEventListener(EVENT, listener);
}
